// Sound effects and zone music with WebAudio: the generated WAVs in Content/Audio,
// pitch jitter, distance falloff and panning, crossfading music. Browsers only start audio after a click or key.
import { settings } from './settings';
import { assetUrl } from './assetUrl';

const HEARING_RANGE = 20 * 16;
const CROSSFADE_SECONDS = 2.5;

interface Track { name: string; source: AudioBufferSourceNode; gain: GainNode; fade: number }

export class Audio {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private readonly buffers = new Map<string, AudioBuffer>();
  private readonly loading = new Map<string, Promise<AudioBuffer | null>>();
  private readonly lastPlayed = new Map<string, number>();
  private current: Track | null = null;
  private wanted: string | null = null;
  private readonly fadingOut: Track[] = [];
  private focused = true;
  listenerX = 0;
  listenerY = 0;

  constructor(private readonly names: string[]) {
    const start = () => this.unlock();
    window.addEventListener('pointerdown', start, { once: false });
    window.addEventListener('keydown', start, { once: false });
    window.addEventListener('blur', () => { this.focused = false; });
    window.addEventListener('focus', () => { this.focused = true; });
  }

  private unlock(): void {
    if (!this.ctx) {
      try {
        this.ctx = new AudioContext();
        this.master = this.ctx.createGain();
        this.master.connect(this.ctx.destination);
        for (const n of this.names) if (n.startsWith('sfx_')) void this.load(n);
      } catch {
        return; // no audio: everything stays silent
      }
    }
    if (this.ctx.state === 'suspended') void this.ctx.resume();
    if (this.wanted && !this.current) this.playMusic(this.wanted, true);
  }

  private load(name: string): Promise<AudioBuffer | null> {
    let p = this.loading.get(name);
    if (!p && this.ctx) {
      const ctx = this.ctx;
      p = fetch(assetUrl(`Content/Audio/${name}.wav`)).then(r => r.arrayBuffer()).then(b => ctx.decodeAudioData(b))
        .then(buf => { this.buffers.set(name, buf); return buf; })
        .catch(() => null);
      this.loading.set(name, p);
    }
    return p ?? Promise.resolve(null);
  }

  private get masterVolume(): number {
    return this.focused || !settings.muteWhenUnfocused ? settings.masterVolume : 0;
  }

  /** A sound effect; pitchJitter varies repeats, minInterval drops repeats that come too fast. pitch is in octaves. */
  play(name: string, volume = 1, pitchJitter = 0.05, pan = 0, pitch = 0, minInterval = 0.03): void {
    const ctx = this.ctx, buf = this.buffers.get(name);
    if (!ctx || !buf || !this.master) return;
    const v = volume * this.masterVolume * settings.effectsVolume;
    if (v <= 0.001) return;
    const now = ctx.currentTime;
    if (now - (this.lastPlayed.get(name) ?? -1) < minInterval) return;
    this.lastPlayed.set(name, now);
    const src = ctx.createBufferSource();
    src.buffer = buf;
    src.playbackRate.value = 2 ** Math.max(-1, Math.min(1, pitch + (Math.random() * 2 - 1) * pitchJitter));
    const gain = ctx.createGain();
    gain.gain.value = Math.min(1, v);
    const panner = ctx.createStereoPanner();
    panner.pan.value = Math.max(-1, Math.min(1, pan));
    src.connect(gain).connect(panner).connect(this.master);
    src.start();
  }

  /** A sound at a world position: quieter with distance, panned left/right of the listener. */
  playAt(name: string, x: number, y: number, volume = 1, pitchJitter = 0.05, minInterval = 0.03): void {
    const dx = x - this.listenerX, dy = y - this.listenerY;
    const dist = Math.hypot(dx, dy);
    if (dist >= HEARING_RANGE) return;
    const falloff = 1 - dist / HEARING_RANGE;
    this.play(name, volume * falloff * falloff, pitchJitter, Math.max(-1, Math.min(1, dx / (12 * 16))) * 0.7, 0, minInterval);
  }

  /** Switches to a looping track, crossfading; null stops the music. */
  playMusic(name: string | null, force = false): void {
    this.wanted = name;
    if (!this.ctx || !this.master || (!force && name === this.current?.name)) return;
    if (this.current) this.fadingOut.push(this.current);
    this.current = null;
    if (!name) return;
    const revived = this.fadingOut.findIndex(t => t.name === name);
    if (revived >= 0) {
      this.current = this.fadingOut.splice(revived, 1)[0];
      return;
    }
    const ctx = this.ctx, master = this.master;
    void this.load(name).then(buf => {
      if (!buf || this.wanted !== name || this.current) return;
      const source = ctx.createBufferSource();
      source.buffer = buf;
      source.loop = true;
      const gain = ctx.createGain();
      gain.gain.value = 0;
      source.connect(gain).connect(master);
      source.start();
      this.current = { name, source, gain, fade: 0 };
    });
  }

  update(dt: number): void {
    if (!this.ctx) return;
    const step = dt / CROSSFADE_SECONDS, music = this.masterVolume * settings.musicVolume;
    const smooth = (x: number) => Math.sin(Math.max(0, Math.min(1, x)) * Math.PI / 2);
    if (this.current) {
      this.current.fade = Math.min(1, this.current.fade + step);
      this.current.gain.gain.value = music * smooth(this.current.fade);
    }
    for (let i = this.fadingOut.length - 1; i >= 0; i--) {
      const t = this.fadingOut[i];
      t.fade -= step;
      if (t.fade <= 0) {
        t.source.stop();
        this.fadingOut.splice(i, 1);
        continue;
      }
      t.gain.gain.value = music * smooth(t.fade);
    }
  }
}
